import React, { useMemo, useState } from 'react'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import ReactModal from 'react-modal'
import '../../styles/modals/RunProgramModal.css'
import { FaTimes } from 'react-icons/fa'
import RunProgramTimePick from './RunProgramTimePick'
import TargetGroupModal from './TargetGroupModal'
import i18n from 'i18next'
import TextField from '@mui/material/TextField'
import deLocale from 'date-fns/locale/de'
import enLocale from 'date-fns/locale/en-US'
import arLocale from 'date-fns/locale/ar-SA'
import { addDays, format } from 'date-fns'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { checkProgramOverlap, runProgram } from '../../server/server'
import { notify, translateTargetText } from '../../utils/util'
import ConfirmationModal from '../modals/ConfirmationModal'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { ReactComponent as Calendar } from '../../assets/svg/CalendarIcon.svg'
import Loading from '../Loading'

const LOCALE_MAP = {
    ar: arLocale,
    en: enLocale,
    de: deLocale,
}
const localeMapFor = (language) => LOCALE_MAP[language] ?? enLocale

/**
 * Day offsets from `startDate`, one per program day, landing only on the
 * selected weekdays. `targetDays` is Mon-first while getDay() is Sun-first,
 * hence the +6 shift (without it every post went out a day early).
 * Empty when no weekday is selected, which would otherwise never terminate.
 */
export const computeDeltaDays = (startDate, targetDays, programDaysLength) => {
    if (!targetDays.some((td) => td.selected) || !(programDaysLength > 0)) return []
    let days = programDaysLength
    let currentDayDiff = 0
    let currentDayWeek = (startDate.getDay() + 6) % 7
    const diffs = []
    while (days) {
        if (targetDays[currentDayWeek % 7].selected) {
            diffs.push(currentDayDiff)
            --days
        }
        ++currentDayDiff
        ++currentDayWeek
    }
    return diffs
}

function RunProgramModal({ showRun, closeRun, programDaysLength, user, programID, programName }) {
    const { t, locale } = useTranslation()
    const [processing, setProcessing] = useState(false)
    useTrackedSurface(ANALYTICS_EVENTS.CONTENT_LIBRARY_RUN_PROGRAM_VIEWED, {
        active: showRun,
        contentId: programID,
        content: programName,
    })
    React.useEffect(() => {
        if (showRun) {
            ReactModal.setAppElement('#root')
            document.getElementById('home').style.filter = 'blur(5px)'
        } else {
            document.getElementById('home').style.filter = 'unset'
        }
    }, [showRun])
    const [returnTarget, setReturnTarget] = useState({
        name: 'Select target',
        target: [],
        selection: [],
        type: 'root',
    })
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [overlapModalOpen, setOverlapModalOpen] = useState(false)
    const [targetDays, setTargetDays] = useState([
        {
            name: 'Mon',
            selected: true,
        },
        {
            name: 'Tue',
            selected: true,
        },
        {
            name: 'Wed',
            selected: true,
        },
        {
            name: 'Thu',
            selected: true,
        },
        {
            name: 'Fri',
            selected: true,
        },
        {
            name: 'Sat',
            selected: false,
        },
        {
            name: 'Sun',
            selected: false,
        },
    ])
    const [startDate, setStartDate] = useState(new Date())
    const [showTarget, setShowTarget] = useState(false)
    const [pickDate, setPickDate] = useState(format(Date.now(), "yyyy-MM-dd'T'HH:mm:ss"))

    const [pickedDate, setPickedDate] = useState({
        poll: { hours: 8, minutes: 0 },
        question: { hours: 8, minutes: 0 },
        feed: { hours: 8, minutes: 0 },
    })
    const handlePickedDateChange = (newDate) => {
        setStartDate(newDate)
    }
    const closeOverlapModal = () => {
        setOverlapModalOpen(false)
    }

    const closeTarget = () => {
        setShowTarget(false)
    }
    const checkOverlap = async () => {
        const response = await checkProgramOverlap({
            program_id: programID,
            target: returnTarget?.target ? returnTarget.target.map((a) => a.id) : [],
            selected_classes: [],
            institution_id: user.institution_id,
        })
        if (response.status === 200) {
            if (response.data.isOverlapping) {
                setOverlapModalOpen(true)
            } else {
                runProg(startDate)
            }
        }
    }
    const runProg = async () => {
        setProcessing(true)
        const response = await runProgram({
            createdDate: new Date().toString(),
            // createdDate: Date.now() + 3 * 60 * 60 * 1000,
            targetDays: targetDays,
            target: returnTarget?.target ? returnTarget.target.map((a) => a.id) : [],
            deltaDays: deltaDays,
            time: pickedDate,
            programId: programID,
            hash: Date.now() + user.id.toString(36),
            startDate: startDate,
            scheduleInfo: {},
            targetText: returnTarget.name,
            selectedClasses: [],
            selectedYears: [],
        })
        if (response.status === 200) {
            notify(t('Program successfully shared'))
            closeRun()
            setProcessing(false)
        } else {
            setProcessing(false)
            const message = response.message ?? 'There is an error...'
            notify(message, 'error')
        }
    }

    const deltaDays = useMemo(() => computeDeltaDays(startDate, targetDays, programDaysLength), [startDate, targetDays, programDaysLength])
    const dateLocale = { locale: localeMapFor(i18n.language) }
    const endDate = deltaDays.length > 0 ? addDays(startDate, deltaDays[deltaDays.length - 1]) : null

    const handleRun = () => {
        if (targetDays.some((td) => td.selected === true)) {
            if (returnTarget?.target?.length === 0) {
                notify(t('youMustSelectTargetGroup'), 'warning')
            } else {
                checkOverlap(startDate)
            }
        } else {
            notify(t('youMustSelectDaysOfWeek'), 'warning')
        }
    }
    const handleSelectedDay = (name, selected, id) => {
        const newDays = [...targetDays]
        newDays[id] = { name: name, selected: !selected }
        setTargetDays(newDays)
    }
    const handleOverlapConfirmation = () => {
        setOverlapModalOpen(false)
        runProg(startDate)
    }
    return (
        <ReactModal
            transparent
            closeTimeoutMS={600}
            isOpen={showRun}
            onAfterOpen={() => {}}
            onRequestClose={closeRun}
            style={{
                overlay: {
                    zIndex: 0,
                },
                content: {
                    padding: 0,
                    opacity: 1,
                    width: 600,
                    minWidth: 600,
                    margin: 'auto',
                    marginTop: '2%',
                    backgroundColor: 'transparent',
                    maxHeight: '90%',
                    border: 'none',
                    margin: 'auto',
                    boxShadow: 'none',
                    overflow: 'hidden',
                },
            }}
        >
            <div className="w-full justify-center flex">
                <div className={`relative bg-white rounded-xl h-full w-full ${i18n.language == 'ar' ? 'ar-lang' : ''}`}>
                    <button
                        type="button"
                        aria-label={t('close')}
                        className="rtg-focus-ring absolute top-4 right-4 z-10 hover:bg-darkenPurple pointer flex bg-deepPurple rounded-full w-[40px] h-[40px] justify-center items-center"
                        onClick={() => closeRun()}
                    >
                        <CloseModalIcon aria-hidden="true" />
                    </button>
                    <div className="pt-10 pb-5 bg-lightBG rounded-xl">
                        <p className="text-deepPurple font-bold text-[25px] text-center">{t('runProgram')}</p>
                        <p className="text-sm text-mudPurple text-center">{programName}</p>
                    </div>
                    <div className="flex justify-center items-center flex-col p-6 w-full">
                        <div className="w-full flex justify-center mb-4">
                            <p className="text-sm text-mudPurple text-center w-[430px]">{t('thisToolHelpsYouPlanTheEntireProgramOnce')}</p>
                        </div>
                        <div className="w-[500px] flex justify-between items-center my-4">
                            <div className="bg-lightBG rounded-full flex items-center px-4 h-[40px]">
                                <p className="text-base text-deselectPurple font-semibold mr-2">{t('targetGroup')}:</p>
                                <div className="" id="target-group-button" onClick={() => setShowTarget(true)}>
                                    <p className="text-base text-deepPurple font-semibold pointer">{translateTargetText(returnTarget.name)}</p>
                                </div>
                            </div>
                            <div className="bg-lightBG relative rounded-full flex items-center px-4 h-[40px]">
                                <div className="flex items-center gap-2 pointer not-selectable" onClick={() => setIsDatePickerOpen(true)}>
                                    <Calendar />
                                    <p className="text-base text-deselectPurple font-semibold"> {t('startDate')}: </p>
                                    <p className="text-base text-deepPurple font-semibold">{format(startDate, 'd MMM yyyy', dateLocale)}</p>
                                </div>
                                <div style={{ position: 'absolute', top: 0, left: 0, visibility: 'hidden' }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={LOCALE_MAP[i18n.language]}>
                                        <DatePicker
                                            open={isDatePickerOpen}
                                            onClose={() => setIsDatePickerOpen(false)}
                                            label="Date desktop"
                                            inputFormat="MM/dd/yyyy"
                                            value={startDate}
                                            onChange={handlePickedDateChange}
                                            renderInput={(params) => <TextField {...params} />}
                                            minDate={new Date()}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>

                        <div className="bg-lightBG rounded-full flex items-center justify-between px-4 h-[40px] w-[500px]">
                            <p className="text-deselectPurple font-semibold text-base whitespace-nowrap mr-4">{t('selectDays')}:</p>
                            <div className="flex flex-row items-center gap-1">
                                {targetDays.map(({ name, selected }, id) => {
                                    return (
                                        <button
                                            type="button"
                                            aria-pressed={selected}
                                            className={`rtg-focus-ring pointer px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                                                selected ? 'bg-deepPurple text-white' : 'text-mudPurple hover:bg-white'
                                            }`}
                                            key={id}
                                            onClick={() => handleSelectedDay(name, selected, id)}
                                        >
                                            {t(name)}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                        <p className="text-sm text-mudPurple text-center mt-3 min-h-[20px]" aria-live="polite">
                            {endDate
                                ? `${t('endsOn', { date: format(endDate, 'EEE d MMM yyyy', dateLocale) })} · ${t('postingDays', { count: programDaysLength })}`
                                : t('youMustSelectDaysOfWeek')}
                        </p>
                        <div className="flex w-full justify-center items-center">
                            <p className="text-deselectPurple font-semibold text-base mr-5 mt-10">{t('pickTime')}: </p>
                            <div className="mt-4">
                                <RunProgramTimePick pickedDate={pickedDate} setPickedDate={setPickedDate} />
                            </div>
                        </div>
                        <div className="w-full flex justify-center my-4">
                            <button
                                className="h-[50px] w-[200px] pointer flex items-center justify-center bg-deepPurple rounded-full"
                                onClick={() => handleRun()}
                                id="cy-program-run-modal"
                                disabled={processing}
                            >
                                {processing ? (
                                    <Loading primaryColor="#9392AD" secondaryColor="#9392AD" size={25} stroke={7} />
                                ) : (
                                    <p className="text-highlightColor text-[18px] leading-7 font-semibold">{t('runProgram')}</p>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                <ConfirmationModal
                    modalIsOpen={overlapModalOpen}
                    closeModal={closeOverlapModal}
                    confirmation={handleOverlapConfirmation}
                    message={t('thisProgaramIsAlreadyRunning')}
                />
                <TargetGroupModal showTarget={showTarget} closeTarget={closeTarget} setReturnTarget={setReturnTarget} />
            </div>
        </ReactModal>
    )
}

export default RunProgramModal
